// screens/Clark/jurisdictions.js — CLARK B3 · STEP 3. ONE jurisdiction vocabulary.
//
// 🔴 THIS FILE NO LONGER CONTAINS A JURISDICTION LIST. It contains a PROJECTION of the canonical
// one. B2 shipped a hand-written flat array of 53 display strings with an explicit note that it
// should be REPLACED — not extended — the moment scope was actually sent to a server, "so there is
// one jurisdiction vocabulary and not two." Scope is sent now, so this is that replacement.
//
// The canonical tree is `@citeclerk/goodlaw/jurisdictions.js` — the Indigo Book T1/T3 tables,
// audited page by page, and the SAME module Steel's picker and Steel's jurisdiction-honesty check
// read. Two lists would not merely be untidy: Steel parses the scope token with
// `apps/steel/server/lib/scope.js`, which matches on IDS ('ny', 'ca9', 'scotus'), while B2's list
// carried DISPLAY NAMES ('New York'). Sending those strings would have hit scope.js's
// `unsupported` branch on every selection — surfaced rather than silently ignored, to its credit,
// but scoping nothing.
//
// 🔴 THE VALUE IS NOW AN ID, NOT A LABEL. Nothing persisted the old display string, because the
// control was display-only until this bundle — so there is no migration to write, only a rule not
// to reintroduce labels on the wire.
//
// 🔴 THIS IMPORT PUT packages/goodlaw INTO THE CITATIONS DOCKER IMAGE. It was not there before —
// the citations Dockerfile copied only packages/ui and apps/main, so this import would have built
// fine locally and failed inside the container. Two COPY lines and one dependency entry landed
// with this change; see ops/deploy/citations/Dockerfile.

import { FEDERAL, STATES, TERRITORIES } from "@citeclerk/goodlaw/jurisdictions.js";

/**
 * The picker's options, in the order a lawyer scans them: everything → the Court that binds
 * everywhere → the circuits → the states and territories.
 *
 * `id` is what goes on the wire and is parsed by apps/steel/server/lib/scope.js.
 * `label` is what the user reads. They are deliberately different things.
 */
export const JURISDICTION_OPTIONS = [
  { id: "all", label: "All jurisdictions" },
  { id: FEDERAL.supreme.id, label: FEDERAL.supreme.name },        // 'scotus'
  // Whole circuits. scope.js treats ca1–ca11 and cadc as circuit selections and resolves the
  // Federal Circuit through T1_TO_CL, so the list is passed through as the tree gives it.
  ...FEDERAL.circuits.map((c) => ({ id: c.id, label: `${c.name} (${c.abbr})` })),
  ...STATES.map((s) => ({ id: s.id, label: s.name })),
  ...TERRITORIES.map((t) => ({ id: t.id, label: t.name })),
];

export const DEFAULT_JURISDICTION = "all";

/** id → label, for rendering a stored scope without re-scanning the list. */
export function jurisdictionLabel(id) {
  return JURISDICTION_OPTIONS.find((j) => j.id === id)?.label ?? "All jurisdictions";
}

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * 🔴 CLARK B6 · STEP 2 — RESEARCH'S SELECTION MODEL, PORTED VERBATIM.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Source: apps/steel/client/src/lib/jurisLabel.js at HEAD. Copied rather than imported (the
 * isolation rule), and copied rather than re-derived so the pill in Clark and the pill in
 * Research read identically for identical selections. Steel's unit tests
 * (client/src/lib/jurisLabel.test.js) cover the same behaviour on their side.
 *
 * 🔴 THE DEFAULT IS `{sel: [], preset: 'all'}` AND IT SCOPES NOTHING. `scopeParam` returns the
 * string 'all', which is exactly what B6 STEP 1 taught the anchor gate to read as "no forum
 * selected" — so the default costs the user nothing and asks them nothing.
 *
 * The single-id form above (`JURISDICTION_OPTIONS` / `jurisdictionLabel`) is NOT deleted: B3's
 * flat <select> is what a narrow viewport still falls back to, and `jurisdictionLabel` renders a
 * scope string that was persisted before this bundle.
 */

/** The scope param sent to the answer API: comma-joined selected T1 ids, or 'all'. */
export function scopeParam({ sel = [] } = {}) {
  return sel.length ? sel.map((e) => e.id).join(",") : "all";
}

export function jurisLabel({ sel = [], preset = null } = {}) {
  if (preset === "fed") return { text: "Federal only", filtered: true };
  if (!sel.length) return { text: "All jurisdictions", filtered: false };
  const ab = (e) => e.abbr || e.name;
  // 🔴 BUNDLE UI · LANE 3 — THE WORD "Jurisdiction" IS GONE FROM THE CHIP.
  // This branch used to return `Jurisdiction: NY`, so the pill read "Jurisdiction: Mississippi ▾"
  // — the runbook's "says the word twice" complaint, one branch down from the one everybody
  // looked at (the empty case already read "All jurisdictions"). The value IS the label; the JOB
  // is carried by the button's aria-label (`Jurisdiction: …`, WCAG 2.5.3) where a screen reader
  // needs it and a sighted user does not.
  // ⚠️ Steel's `client/src/lib/jurisLabel.js` — the file this was ported from — still returns the
  // prefixed form, so Research's pill and Clark's now differ for 1–2 selections. Flagged in
  // MORNING_REPORT; Research's copy is outside this bundle's fence.
  if (sel.length <= 2) return { text: sel.map(ab).join(" + "), filtered: true };
  if (sel.length === 3) return { text: sel.map(ab).join(" + "), filtered: true };
  return { text: `${sel.length} jurisdictions`, filtered: true };
}

/** The zero-friction default. No prompt, no interstitial — the user may narrow, never must. */
export const DEFAULT_JURIS_SEL = { sel: [], preset: "all" };

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * 🔴 BUNDLE UI · LANE 3 — THE PER-TURN SCOPE STAMP.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Renders a WIRE scope value ('all', or comma-joined T1 ids) as a label. This is the read side of
 * a stamp that was already being written and was being read by NOBODY:
 *
 *   · `clarkAnswerShape.js:171` sets `scope` on the answer contract from the request.
 *   · `clarkAnswerSnapshot.js` freezes it (`scope: contract.scope ?? "all"`) with the message.
 *   · `answerApi.js:134` puts it on the live turn object.
 *   · …and nothing on the client rendered it. Grep for `.scope` across the Clark screen before
 *     this bundle returns zero read sites. A field computed, persisted, and consumed by nobody.
 *
 * 🔴 WHY THIS IS NOT COSMETIC. The jurisdiction picker persists and is changeable mid-chat. A
 * sibling ran ten questions at two scopes and SEVEN OF TEN changed verdict on the scope variable
 * alone, six of them getting worse — scope changes RETRIEVAL, not just labelling. Without a
 * per-turn stamp, changing the picker silently invalidates every answer above it and nothing on
 * screen says so. With it, each turn states the scope it was answered under and a mid-chat change
 * affects only the turns after it.
 *
 * Returns null for the unscoped default, because a stamp on every turn saying "All jurisdictions"
 * is noise — the absence of a narrowing is the absence of a claim.
 */
export function scopeStampLabel(wireScope) {
  const raw = typeof wireScope === "string" ? wireScope.trim() : "";
  if (!raw || raw === "all") return null;
  const ids = raw.split(",").map((s) => s.trim()).filter(Boolean);
  if (!ids.length) return null;
  if (ids.length > 3) return `${ids.length} jurisdictions`;
  // 🔴🔴 BUNDLE V7 · LANE 4 RULE 1 — AN UNRECOGNISED ID MUST NOT RENDER AS "All jurisdictions".
  // `jurisdictionLabel` falls back to "All jurisdictions" for any id it cannot find, which is the
  // right default for a PICKER (nothing chosen yet) and exactly the wrong one for a STAMP: the
  // stamp is a claim about the scope an answer was actually retrieved under, and the fallback
  // makes an unknown id claim the BROADEST possible coverage. Since line 122 already returns null
  // for the real "all", any stamp reading "All jurisdictions" is, by construction, this bug.
  //
  // Caught by a fixture carrying an uppercase "MS": the turn rendered "All jurisdictions" and
  // looked entirely correct. Case, a renamed id or a server that widens its vocabulary would all
  // do the same thing silently, and it fails toward over-claiming.
  //
  // This is the same moat rule the treatment pill already follows — "an unknown or missing flag is
  // PENDING, never clear … never inferred from the absence of a signal". If ANY id fails to
  // resolve we make NO claim at all rather than a partial or wrong one; no stamp is honest,
  // a confident wrong one is not.
  const labels = ids.map((id) => JURISDICTION_OPTIONS.find((j) => j.id === id)?.label ?? null);
  if (labels.some((l) => l === null)) return null;
  return labels.join(" + ");
}
